import { memo, useMemo } from 'react';

import { ChakraBox, ChakraFlex } from '@equip.health/ui';

import MaiChatChartCitationPill from './MaiChatChartCitationPill';
import MaiChatCitationPill from './MaiChatCitationPill';
import MaiChatFeedbackBar from './MaiChatFeedbackBar';
import MaiChatThinking from './MaiChatThinking';
import MaiChatTimestamp from './MaiChatTimestamp';
import MaiChatToolSteps from './MaiChatToolSteps';
import MaudMark from './MaudMark';
import { MAI_CHAT_MARKDOWN_STYLES } from './maiChatMarkdown.styles';
import {
  MaiCitation,
  MaiMessageSegment,
  MaiMessageStatus,
} from './maiChat.types';
import { MaiFeedbackRating } from '~/lib/types/mai/MaiFeedbackRating';
import {
  collectChartCitations,
  extractCitations,
  renderMaiMarkdown,
} from './maiChat.util';

interface MaiChatAiMessageProps {
  messageId: string;
  segments: MaiMessageSegment[];
  status: MaiMessageStatus;
  createdAt: string;
  citations?: MaiCitation[];
  serverMessageId?: string;
  rating?: MaiFeedbackRating;
  isRatingSubmitting?: boolean;
  onRate: (serverMessageId: string, rating: MaiFeedbackRating) => void;
  onRegenerate: (messageId: string) => void;
  onToolStepsToggle: (
    isExpanded: boolean,
    toolCount: number,
    serverMessageId?: string,
  ) => void;
}

const MaiChatTextSegment = memo(({ text }: { text: string }) => {
  const html = useMemo(() => {
    const { text: cleaned } = extractCitations(text);
    return renderMaiMarkdown(cleaned);
  }, [text]);
  return (
    <ChakraBox
      data-testid="mai-chat-ai-message__text"
      color="neutral.primary"
      fontSize="14px"
      lineHeight="1.5"
      sx={MAI_CHAT_MARKDOWN_STYLES}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});
MaiChatTextSegment.displayName = 'MaiChatTextSegment';

const MaiChatAiMessage = memo(
  ({
    messageId,
    segments,
    status,
    createdAt,
    citations,
    serverMessageId,
    rating,
    isRatingSubmitting,
    onRate,
    onRegenerate,
    onToolStepsToggle,
  }: MaiChatAiMessageProps) => {
    const isStreaming = status === 'streaming';
    // The timestamp is the completion time, which only a finished response has.
    const isComplete = status === 'sent';
    const lastSegment = segments[segments.length - 1];
    const hasPendingTool =
      lastSegment?.kind === 'tools' &&
      lastSegment.toolCalls.some((tool) => tool.status === 'pending');
    const isThinking =
      isStreaming && lastSegment?.kind !== 'text' && !hasPendingTool;
    const isThinkingInSteps = isThinking && lastSegment?.kind === 'tools';
    const lastIndex = segments.length - 1;

    const chartCitations = useMemo(
      () =>
        collectChartCitations(
          segments
            .filter(
              (s): s is { kind: 'text'; text: string } => s.kind === 'text',
            )
            .map((s) => s.text),
        ),
      [segments],
    );

    const kbCitations = citations ?? [];
    const hasCitations = chartCitations.length > 0 || kbCitations.length > 0;

    return (
      <ChakraFlex
        data-testid="mai-chat-ai-message"
        role="article"
        aria-label="Maud response"
        alignItems="flex-start"
        gap="10px"
        maxWidth="92%"
      >
        <MaudMark size={22} />
        <ChakraFlex direction="column" gap="8px" flex="1" minWidth={0}>
          {segments.map((segment, index) =>
            segment.kind === 'text' ? (
              <MaiChatTextSegment key={`text-${index}`} text={segment.text} />
            ) : (
              <MaiChatToolSteps
                key={`tools-${index}`}
                toolCalls={segment.toolCalls}
                isThinking={isThinkingInSteps && index === lastIndex}
                onToggle={(isExpanded) =>
                  onToolStepsToggle(
                    isExpanded,
                    segment.toolCalls.length,
                    serverMessageId,
                  )
                }
              />
            ),
          )}
          {isThinking && !isThinkingInSteps && <MaiChatThinking />}
          {hasCitations && (
            <ChakraFlex
              data-testid="mai-chat-ai-message__citations"
              direction="column"
              gap="4px"
            >
              <ChakraBox
                fontSize="11px"
                fontWeight={600}
                color="neutral.tertiary"
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                Sources
              </ChakraBox>
              <ChakraFlex flexWrap="wrap" gap="6px">
                {chartCitations.map((citation) => (
                  <MaiChatChartCitationPill
                    key={`chart-${citation.type}|${citation.date}|${citation.source}`}
                    citation={citation}
                  />
                ))}
                {kbCitations.map((citation, index) => (
                  <MaiChatCitationPill
                    key={`kb-${citation.url}-${index}`}
                    citation={citation}
                  />
                ))}
              </ChakraFlex>
            </ChakraFlex>
          )}
          {!isStreaming && (
            <ChakraFlex alignItems="center" gap="12px">
              {isComplete && <MaiChatTimestamp createdAt={createdAt} />}
              <MaiChatFeedbackBar
                messageId={messageId}
                serverMessageId={serverMessageId}
                rating={rating}
                isSubmitting={isRatingSubmitting}
                onRate={onRate}
                onRegenerate={onRegenerate}
              />
            </ChakraFlex>
          )}
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);
MaiChatAiMessage.displayName = 'MaiChatAiMessage';

export default MaiChatAiMessage;
